import * as React from "react";
import {Button, Grid, IconButton, MenuItem, TextField, Typography} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {getSubmitButtonProps} from "../../../../utils/SubmitButtonProps";
import {getApiErrorMessage} from "../../../../utils/apiError";
import {
    emptyFollowUpIssue,
    FollowUpCategory,
    findFollowUpReason,
    FOLLOW_UP_REASONS,
    IFollowUpIssueInput,
    IFollowUpIssueRow,
    MAX_FOLLOW_UP_ISSUES,
    MAX_FOLLOW_UP_QUESTION_LENGTH,
    toFollowUpRequestIssues,
    validateFollowUpIssues,
} from "./followUp";

const APPROVED = "APPROVED";
const FOLLOW_UP = "FOLLOW_UP";

interface IProps {
    initialPersonId?: string;
    initialPrescriptionId?: string;
    autoSubmit?: boolean;
    // When set, the Submit button uses this background color (used to make it
    // stand out when embedded in the NewRx workflow). Standalone usage omits it.
    submitColor?: string;
    // Called with the response after a successful approve (used by the NewRx
    // workflow to collapse this form into a completed summary container). The
    // second argument is the prescriptionId that was actually submitted (may be
    // user-entered when no match was prefilled), so callers can carry it forward.
    onSuccess?: (response: any, prescriptionId: string) => void;
    // "approve" (default): approve only, for callers that move on to ordering after a success.
    // "followUp": route to follow-up only. "either": a Status dropdown offers both.
    mode?: "approve" | "followUp" | "either";
    // Approve one prescription only, never every prescription on the person.
    requirePrescriptionId?: boolean;
    onFollowUp?: (response: any, prescriptionId: string, issues: IFollowUpIssueInput[]) => void;
}

interface IState {
    personId: string;
    prescriptionId: string;
    status: string;
    followUpIssues: IFollowUpIssueRow[];
    isLoading: boolean;
    errorMessage: string;
    response: any;
}

const CATEGORIES = Object.values(FollowUpCategory);

class CcsStatusUpdateSection extends React.Component<IProps, IState> {
    // The last prescription ID this component filled in from props.
    private seededPrescriptionId: string;

    constructor(props: IProps) {
        super(props);
        this.seededPrescriptionId = props.initialPrescriptionId || '';
        this.state = {
            personId: props.initialPersonId || '',
            prescriptionId: props.initialPrescriptionId || '',
            status: props.mode === "followUp" ? FOLLOW_UP : APPROVED,
            followUpIssues: [emptyFollowUpIssue()],
            isLoading: false,
            errorMessage: '',
            response: ''
        };
    }

    // A later lookup result fills the field only if the user hasn't typed their own ID.
    public componentDidUpdate() {
        const next = this.props.initialPrescriptionId || '';
        const untouched = !this.state.prescriptionId || this.state.prescriptionId === this.seededPrescriptionId;
        if (next && next !== this.seededPrescriptionId && untouched) {
            this.seededPrescriptionId = next;
            this.setState({prescriptionId: next});
        }
    }

    public componentDidMount() {
        if (this.props.autoSubmit && this.props.initialPersonId) {
            this.handleSubmit();
        }
    }

    public render() {
        const isFollowUp = this.state.status === FOLLOW_UP;
        return (
            <Grid container direction="column" justify="center" alignItems="center" spacing={3}>
                <Grid item xs>
                    <Alert variant="filled" severity="info">
                        {isFollowUp
                            ? "CCS Status Update: Route one prescription at RphCheck to follow-up with one or more issues."
                            : "CCS Status Update: Approve clinical checking for prescriptions. " +
                              "Provide a Person ID to process all prescriptions, or optionally specify a single Prescription ID."}
                    </Alert>
                </Grid>

                <Grid item xs>
                    <TextField
                        id="ccs-person-id"
                        style={{width: 500}}
                        label="Person ID (Required)"
                        variant="outlined"
                        value={this.state.personId}
                        onChange={(e) => this.setState({personId: e.target.value})}
                    />
                </Grid>

                <Grid item xs>
                    <TextField
                        id="ccs-prescription-id"
                        style={{width: 500}}
                        label={isFollowUp || this.props.requirePrescriptionId ? "Prescription ID (Required)" : "Prescription ID (Optional)"}
                        variant="outlined"
                        value={this.state.prescriptionId}
                        onChange={(e) => this.setState({prescriptionId: e.target.value, errorMessage: ''})}
                    />
                </Grid>

                {this.props.mode !== "followUp" && (
                    <Grid item xs>
                        <TextField
                            select
                            id="ccs-status"
                            style={{width: 500}}
                            label="Status (Required)"
                            variant="outlined"
                            value={this.state.status}
                            onChange={(e) => this.setState({status: e.target.value, errorMessage: ''})}
                        >
                            <MenuItem value={APPROVED}>APPROVED</MenuItem>
                            {this.props.mode === "either" && <MenuItem value={FOLLOW_UP}>ROUTE TO FOLLOW-UP</MenuItem>}
                        </TextField>
                    </Grid>
                )}

                {isFollowUp && this.renderFollowUpIssues()}

                <Grid item xs>
                    <Button
                        {...getSubmitButtonProps(this.props.submitColor)}
                        onClick={() => this.handleSubmit()}
                    >
                        {isFollowUp ? "Route to Follow-Up" : "Submit"}
                    </Button>
                </Grid>

                {this.state.isLoading && <LoadingSpinner/>}

                {this.state.errorMessage && (
                    <Alert variant="filled" severity="error">
                        Error: {this.state.errorMessage}
                    </Alert>
                )}

                {this.state.response && (
                    <div>
                        <br/>
                        <h4>Response</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={this.state.response} printHtml={false}/>
                        </p>
                    </div>
                )}
            </Grid>
        );
    }

    private renderFollowUpIssues() {
        const issues = this.state.followUpIssues;
        return (
            <Grid item xs style={{width: 800}}>
                <Typography variant="subtitle2" gutterBottom>Follow-up issues</Typography>
                <Typography variant="caption" color="textSecondary" component="p" style={{marginBottom: 16}}>
                    Add at least one issue (up to {MAX_FOLLOW_UP_ISSUES}). Each issue needs a reason and a question.
                </Typography>
                {issues.map((issue, index) => {
                    const usedElsewhere = new Set(
                        issues.filter((_, i) => i !== index).map((other) => other.reasonCode));
                    const selected = findFollowUpReason(issue.reasonCode);
                    return (
                        <div key={issue.rowId} style={{display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 16}}>
                            <TextField
                                select
                                id={`follow-up-reason-${index}`}
                                style={{width: 340}}
                                label={`Reason ${index + 1} (Required)`}
                                variant="outlined"
                                value={issue.reasonCode}
                                helperText={selected
                                    ? selected.category + (selected.automatedPrescriberOutreach
                                        ? ". Uses automated prescriber outreach" : "")
                                    : " "}
                                onChange={(e) => this.updateIssue(index, {reasonCode: e.target.value})}
                            >
                                {CATEGORIES.map((category) => [
                                    <MenuItem key={category} disabled style={{fontWeight: "bold", opacity: 1}}>
                                        {category}
                                    </MenuItem>,
                                    ...FOLLOW_UP_REASONS.filter((reason) => reason.group === category).map((reason) => (
                                        <MenuItem
                                            key={reason.code}
                                            value={reason.code}
                                            disabled={usedElsewhere.has(reason.code)}
                                            style={{paddingLeft: 32}}
                                        >
                                            {reason.label}
                                        </MenuItem>
                                    )),
                                ])}
                            </TextField>
                            <TextField
                                id={`follow-up-question-${index}`}
                                style={{flex: 1}}
                                label={`Question ${index + 1} (Required)`}
                                variant="outlined"
                                multiline
                                value={issue.followupQuestion}
                                helperText={`${issue.followupQuestion.length}/${MAX_FOLLOW_UP_QUESTION_LENGTH}`}
                                inputProps={{maxLength: MAX_FOLLOW_UP_QUESTION_LENGTH}}
                                onChange={(e) => this.updateIssue(index, {followupQuestion: e.target.value})}
                            />
                            <IconButton
                                aria-label={`Remove issue ${index + 1}`}
                                disabled={issues.length === 1}
                                onClick={() => this.removeIssue(index)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                        </div>
                    );
                })}
                <Button
                    startIcon={<AddIcon/>}
                    disabled={issues.length >= MAX_FOLLOW_UP_ISSUES}
                    onClick={() => this.setState({followUpIssues: [...issues, emptyFollowUpIssue()]})}
                >
                    Add issue
                </Button>
            </Grid>
        );
    }

    private updateIssue(index: number, change: Partial<IFollowUpIssueInput>) {
        this.setState({
            followUpIssues: this.state.followUpIssues.map((issue, i) => (i === index ? {...issue, ...change} : issue)),
            errorMessage: '',
        });
    }

    private removeIssue(index: number) {
        this.setState({followUpIssues: this.state.followUpIssues.filter((_, i) => i !== index), errorMessage: ''});
    }

    private handleSubmit() {
        if (this.state.isLoading) {
            return;
        }

        if (!this.state.personId) {
            alert('Please enter a Person ID');
            return;
        }

        const isFollowUp = this.state.status === FOLLOW_UP;
        const needsPrescriptionId = isFollowUp || !!this.props.requirePrescriptionId;
        const prescriptionId = needsPrescriptionId ? this.state.prescriptionId.trim() : this.state.prescriptionId;
        const body: any = {personId: this.state.personId};
        if (prescriptionId) {
            body.prescriptionId = prescriptionId;
        }

        if (needsPrescriptionId && !prescriptionId) {
            this.setState({
                errorMessage: isFollowUp
                    ? "Enter the Prescription ID to route to follow-up."
                    : "Enter the Prescription ID to approve.",
            });
            return;
        }

        let issues: IFollowUpIssueInput[] = [];
        if (isFollowUp) {
            const problem = validateFollowUpIssues(this.state.followUpIssues);
            if (problem) {
                this.setState({errorMessage: problem});
                return;
            }
            issues = toFollowUpRequestIssues(this.state.followUpIssues);
            body.status = "REJECTED";
            body.followUpIssues = issues;
        } else {
            body.status = APPROVED;
        }

        this.setState({isLoading: true, errorMessage: '', response: ''});

        API.post('ApaqPortalAPI', '/ccs/status-update', {body, headers: {}})
            .then((response: any) => {
                this.setState({response, isLoading: false});
                if (isFollowUp) {
                    this.props.onFollowUp?.(response, prescriptionId, issues);
                } else {
                    this.props.onSuccess?.(response, prescriptionId);
                }
            })
            .catch((error) => {
                this.setState({
                    errorMessage: getApiErrorMessage(error, "Failed to update the CCS status."),
                    isLoading: false
                });
            });
    }
}

export default CcsStatusUpdateSection;
